import React, { useState, useLayoutEffect, useContext } from 'react';
import { rawTimeZones } from '@vvo/tzdb';

import * as S from './styles';
import { DataFormProps } from 'types/InputsStoriesCarouselProps';
import SimpleInput from 'components/SimpleInput';
import { SelectInputItemProps } from 'types/SelectInputItemProps';
import SelectInput from 'components/SelectInput';
import { optionsType } from 'utils/optionsInputs';
import Check from 'components/Buttons/Check';
import { formatDateToISO, formatToDateCustom } from 'utils/functions';
import DynamicPageContext from 'context/DynamicPage';

interface DataFormStoriesCarouselProps {
	dataForm: DataFormProps;
	setDataForm: React.Dispatch<React.SetStateAction<DataFormProps>>;
	isShow: boolean;
}

export default function DataFormStoriesCarousel({
	isShow,
	dataForm,
	setDataForm
}: DataFormStoriesCarouselProps) {
	const { moduleInSavingMode, moduleOnPhoneToEdit } =
		useContext(DynamicPageContext);

	const [isShowDateInputs, setIsShowDateInputs] = useState(false);

	// Função para converter minutos para formato GMT±X
	const formatGMT = (offsetMinutes: number): string => {
		const hours = Math.floor(offsetMinutes / 60);
		const minutes = Math.abs(offsetMinutes % 60);
		const sign = offsetMinutes >= 0 ? '+' : '-';
		return `GMT${sign}${Math.abs(hours)}${minutes ? `:${minutes}` : ''}`;
	};

	// Criando a lista com GMT formatado
	const timeZoneOptions: SelectInputItemProps[] = rawTimeZones.map((tz) => ({
		id: tz.name, // Exemplo: "America/Sao_Paulo"
		option: `${tz.name} (${formatGMT(tz.rawOffsetInMinutes)})` // Exemplo: "America/Sao_Paulo (GMT-3)"
	}));

	function findOptionOnListById(value: string, list: SelectInputItemProps[]) {
		const itemsList = list.filter((item) => item.id === value);
		if (itemsList.length > 0) {
			return itemsList[0];
		}

		return '';
	}

	//reseta os dados de agendamento os inputs não estejam visiaveis
	useLayoutEffect(() => {
		if (!isShowDateInputs) {
			setDataForm((obj) => {
				if (obj.schedule) {
					return {
						...obj,
						schedule: {
							...obj.schedule,
							start: '',
							end: '',
							timezone: '',
							countdown: false
						}
					};
				}

				return obj;
			});
		}
	}, [isShowDateInputs]);

	//se estiver em edição aqui pega os dados do item editado
	useLayoutEffect(() => {
		if (moduleOnPhoneToEdit !== null && moduleInSavingMode === null) {
			setTimeout(() => {
				setDataForm((obj) => ({ ...obj, ...moduleOnPhoneToEdit.data }));
			}, 100);

			if (moduleOnPhoneToEdit?.data?.schedule) {
				setIsShowDateInputs(true);
			}
		}
	}, []);

	return (
		<S.container $isShow={isShow}>
			<S.container $isShow={isShow}>
				<S.twoInputs>
					<SimpleInput
						label="Título *"
						initialValue={dataForm.title}
						getValue={(value: string) => {
							setDataForm((obj) => ({ ...obj, title: value }));
						}}
					/>

					<SimpleInput
						paddingInLineEnd
						label="Size "
						typeInput="number"
						initialValue={dataForm.size}
						getValue={(value: string) => {
							setDataForm((obj) => ({ ...obj, size: parseInt(value) }));
						}}
					/>
				</S.twoInputs>

				<S.twoInputs>
					<SimpleInput
						typeInput="number"
						paddingInLineEnd
						label="Padding Top"
						initialValue={dataForm.paddingTop}
						getValue={(value: string) => {
							setDataForm((obj) => ({ ...obj, paddingTop: parseInt(value) }));
						}}
					/>

					<SimpleInput
						typeInput="number"
						paddingInLineEnd
						label="Padding Bottom"
						initialValue={dataForm.paddingBottom}
						getValue={(value: string) => {
							setDataForm((obj) => ({
								...obj,
								paddingBottom: parseInt(value)
							}));
						}}
					/>
				</S.twoInputs>

				<S.twoInputs>
					<div style={{ display: 'none' }}>
						<Check
							text="Programação"
							isChecked={isShowDateInputs}
							setIsChecked={() => {
								setIsShowDateInputs((obj) => !obj);
							}}
						/>
					</div>

					<Check
						text="Quadrado?"
						isChecked={dataForm.square}
						setIsChecked={() => {
							setDataForm((obj) => ({ ...obj, square: !obj.square }));
						}}
					/>
				</S.twoInputs>

				<div style={{ display: 'none' }}>
					{isShowDateInputs && (
						<>
							<SelectInput
								isHide
								list={timeZoneOptions}
								getValue={() => {
									setDataForm((obj) => {
										if (obj.schedule) {
											return {
												...obj,
												schedule: {
													...obj.schedule,
													timezone: 'America/Sao_Paulo'
												}
											};
										}

										return { ...obj };
									});
								}}
								initialValue={
									dataForm.schedule?.timezone === ''
										? ''
										: findOptionOnListById(
												dataForm.schedule?.timezone || '',
												optionsType
											)
								}
								label="Selecione a timezone"
							/>

							<S.twoInputs>
								<SimpleInput
									typeInput="datetime-local"
									label="Fim"
									initialValue={
										dataForm.schedule === null
											? ''
											: formatToDateCustom(dataForm.schedule.start)
									}
									getValue={(value: string) => {
										setDataForm((obj) => {
											if (obj.schedule) {
												return {
													...obj,
													schedule: {
														...obj.schedule,
														start: formatDateToISO(value)
													}
												};
											}

											return { ...obj };
										});
									}}
									paddingInLineEnd
								/>

								<SimpleInput
									typeInput="datetime-local"
									label="Fim"
									initialValue={
										dataForm.schedule === null
											? ''
											: formatToDateCustom(dataForm.schedule.end)
									}
									getValue={(value: string) => {
										setDataForm((obj) => {
											if (obj.schedule) {
												return {
													...obj,
													schedule: {
														...obj.schedule,
														end: formatDateToISO(value)
													}
												};
											}

											return { ...obj };
										});
									}}
									paddingInLineEnd
								/>
							</S.twoInputs>

							<Check
								text="Contagem regressiva"
								isChecked={dataForm.schedule?.countdown}
								setIsChecked={() => {
									setDataForm((obj) => {
										if (obj.schedule) {
											return {
												...obj,
												schedule: {
													...obj.schedule,
													countdown: !obj.schedule.countdown
												}
											};
										}
										return { ...obj };
									});
								}}
							/>
						</>
					)}
				</div>
			</S.container>
		</S.container>
	);
}
